import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Box,
  Button,
  ButtonLoadingAnimation,
  Col,
  getCSSVariable,
  Icons,
  Text,
} from '@opendoor/bricks-next';
import type { EntryComponent } from 'cms/entries/entries';
import NewSellerFlowAddressInput from 'components/shared/NewSellerFlowAddressInput';
import type { IFcHero } from 'declarations/contentful';

import { FCContentContainer, FCContentContainerWrapper } from './shared/FCLayout';
import { resizeImageUrl } from './shared/utils';

const HeroBackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;

  @media (min-width: 768px) {
    object-position: center center;
  }
`;

const StyledAddressBox = styled(Box)`
  div > div {
    border-color: ${getCSSVariable('$borderAccentBrand')} !important;
  }
`;

// override the button styles for the address entry
const StyledActionButton = styled(Button)`
  height: 60px !important;
  /* make the button actually line up properly — use position instead of
     transform so press animations don’t clobber the offset */
  position: relative;
  top: -17.5px;
  left: 14px;
`;

const RenderFCHero = (entry: IFcHero) => {
  const { fields } = entry;

  const mobileBackgroundUrl = fields.mobileHeroImageUrl;
  const desktopBackgroundUrl = fields.desktopHeroImageUrl;
  const fallbackBackgroundUrl = mobileBackgroundUrl || desktopBackgroundUrl;

  return (
    <>
      <Global
        styles={css`
          html {
            scroll-behavior: smooth;
          }
        `}
      />
      <FCContentContainerWrapper
        position="relative"
        height="70vh"
        zIndex={2}
        $largerThanSM={{ maxHeight: 600 }}
      >
        {fallbackBackgroundUrl && (
          <picture>
            {desktopBackgroundUrl && (
              <source
                media="(min-width: 768px)"
                srcSet={resizeImageUrl(desktopBackgroundUrl, 2800)}
              />
            )}
            <HeroBackgroundImage
              src={resizeImageUrl(fallbackBackgroundUrl, 1536)}
              alt=""
              loading="eager"
            />
          </picture>
        )}
        <FCContentContainer
          position="relative"
          zIndex={1}
          justifyContent="flex-end"
          paddingVertical="$15x"
          $largerThanSM={{
            paddingVertical: '$16x',
            justifyContent: 'center',
          }}
        >
          {/* White module card */}
          <Col
            backgroundColor="$backgroundPrimary"
            borderRadius="$6x"
            padding="$12x"
            position="relative"
            zIndex={10}
            width="100%"
            $largerThanSM={{
              width: 554,
              alignSelf: 'flex-start',
              padding: '$24x',
              transformOrigin: 'left center',
              gap: '$24x',
            }}
            gap="$12x"
          >
            <Col gap="$8x">
              <Text
                tag="h1"
                typography="$headerMedium"
                color="$contentPrimary"
                $largerThanSM={{
                  typography: '$displaySmall',
                }}
              >
                {fields.title}
              </Text>
              <Text
                tag="p"
                typography="$bodyMedium"
                color="$contentPrimary"
                maxWidth={530}
                $largerThanSM={{ typography: '$bodyXlarge' }}
              >
                {fields.subtitle}
              </Text>
            </Col>

            <StyledAddressBox>
              <NewSellerFlowAddressInput
                placeholder={fields.addressPlaceholderText}
                hideLabel
                inputLocation="hero-cta"
                trackingTaxonomy="new_homepage"
                actionButton={(onPress, isLoading) => (
                  // Render both the desktop CTA button and the mobile suffix
                  // icon, toggled by CSS, so SSR and hydration produce the same
                  // DOM regardless of viewport.
                  <>
                    <Box display="none" $largerThanSM={{ display: 'flex' }}>
                      <StyledActionButton
                        variant="primary"
                        size="medium"
                        label={isLoading ? 'Loading' : fields.ctaButtonText}
                        analyticsName="cosmos-fc-hero-cta"
                        disabled={isLoading}
                        onPress={(e) => {
                          e?.stopPropagation?.();
                          onPress?.();
                        }}
                      />
                    </Box>
                    <Box $largerThanSM={{ display: 'none' }}>
                      {isLoading ? (
                        <Box position="relative" px="$4" display="block">
                          <ButtonLoadingAnimation variant="secondary" size="xsmall" />
                        </Box>
                      ) : (
                        <Icons.Search />
                      )}
                    </Box>
                  </>
                )}
              />
            </StyledAddressBox>
          </Col>
        </FCContentContainer>
      </FCContentContainerWrapper>
    </>
  );
};

const FCHero: EntryComponent<IFcHero> = {
  render: RenderFCHero,
};

export default FCHero;
